import React, { useState, useEffect, useCallback } from 'react';
import { Squares2X2Icon, Bars3Icon, XMarkIcon, WrenchScrewdriverIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useAgrupamentos } from '../hooks/useAgrupamentos';
import { useIsMobile } from '../hooks/useIsMobile';
import { ModalCriarAgrupamento } from '../components/agrupamento/ModalCriarAgrupamento';
import { ManutencaoProdutosGrid } from '../components/agrupamento/ManutencaoProdutosGrid';
import { ListaAgrupamentos } from '../components/agrupamento/ListaAgrupamentos';
import { agrupamentoService } from '../api/agrupamento';
import { PainelGrupoSelecionado } from '../components/agrupamento/PainelGrupoSelecionado';
import type { Agrupamento } from '../api/agrupamento';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';

type AbaInicial = 'grupo' | 'adicionar';

const AgrupamentoPage: React.FC = () => {
  const isMobile = useIsMobile();
  const confirm = useConfirmDialog();
  const [mostrarModalCriar, setMostrarModalCriar] = useState(false);
  const [sidebarAberta, setSidebarAberta] = useState(true);
  const [agrupamentoSelecionado, setAgrupamentoSelecionado] = useState<Agrupamento | null>(null);
  const [abaInicial, setAbaInicial] = useState<AbaInicial>('grupo');

  const {
    agrupamentos,
    agrupamentosAll,
    sugestoesMap,
    carregando,
    termoBusca,
    setTermoBusca,
    carregarAgrupamentos,
    criarAgrupamento,
    excluirAgrupamento,
    renomearAgrupamento,
    decrementarSugestao,
  } = useAgrupamentos();

  useEffect(() => {
    carregarAgrupamentos();
  }, [carregarAgrupamentos]);

  // No mobile, ao selecionar grupo, fecha a sidebar automaticamente
  // Ao fechar grupo, reabre a sidebar
  const handleSelecionarAgrupamento = useCallback((agrupamento: Agrupamento) => {
    setAbaInicial('grupo');
    setAgrupamentoSelecionado(prev => {
      const toggling = prev?.grupoId === agrupamento.grupoId;
      if (isMobile) {
        if (toggling) {
          setSidebarAberta(true);
          return null;
        }
        setSidebarAberta(false);
        return agrupamento;
      }
      return toggling ? null : agrupamento;
    });
  }, [isMobile]);

  const handleAbrirSugestao = useCallback((agrupamento: Agrupamento) => {
    setAbaInicial('adicionar');
    setAgrupamentoSelecionado(agrupamento);
    if (isMobile) setSidebarAberta(false);
  }, [isMobile]);

  const handleFecharGrupo = useCallback(() => {
    setAgrupamentoSelecionado(null);
    if (isMobile) setSidebarAberta(true);
  }, [isMobile]);

  const handleAbrirManutencao = useCallback(() => {
    setAgrupamentoSelecionado(null);
    setSidebarAberta(false);
  }, []);

  const handleCriarConfirmado = useCallback(
    async (descricao: string) => {
      const ok = await criarAgrupamento(descricao);
      if (ok) setMostrarModalCriar(false);
      return ok;
    },
    [criarAgrupamento],
  );

  const handleExcluirAgrupamento = useCallback(
    async (agrupamento: Agrupamento) => {
      const ok = await confirm({
        mensagem: `Excluir o grupo "${agrupamento.descricao}"?`,
        destrutivo: true,
      });
      if (!ok) return;
      await excluirAgrupamento(agrupamento);
      if (agrupamentoSelecionado?.grupoId === agrupamento.grupoId) {
        setAgrupamentoSelecionado(null);
      }
    },
    [confirm, excluirAgrupamento, agrupamentoSelecionado],
  );

  const handleRenomearAgrupamento = useCallback(
    async (agrupamento: Agrupamento, novaDescricao: string) => {
      const ok = await renomearAgrupamento(agrupamento.grupoId, novaDescricao);
      if (ok && agrupamentoSelecionado?.grupoId === agrupamento.grupoId) {
        // Reflete o novo nome no painel selecionado sem esperar próximo carregamento
        setAgrupamentoSelecionado(prev =>
          prev ? { ...prev, descricao: novaDescricao.trim() } : prev,
        );
      }
      return ok;
    },
    [agrupamentoSelecionado, renomearAgrupamento],
  );

  const handleAgrupamentosChange = useCallback(async (produtosAdicionados?: number) => {
    const grupoId = agrupamentoSelecionado?.grupoId;
    await carregarAgrupamentos();
    if (produtosAdicionados && grupoId) {
      decrementarSugestao(grupoId, produtosAdicionados);
    }
    agrupamentoService.recalcularSugestoes()
      .then(() => carregarAgrupamentos())
      .catch(() => {});
  }, [carregarAgrupamentos, decrementarSugestao, agrupamentoSelecionado]);

  const sugestaoAtual = agrupamentoSelecionado ? sugestoesMap[agrupamentoSelecionado.grupoId] : undefined;

  // Mobile: mostra sidebar OU conteúdo (nunca os dois)
  // Desktop: mostra sidebar + conteúdo lado a lado
  const mostrarSidebar = isMobile ? sidebarAberta : sidebarAberta;
  const mostrarConteudo = isMobile ? !sidebarAberta : true;

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-app">
      {/* Header */}
      <div className="flex-none bg-surface border-b border-line px-3 py-2 md:px-4 md:py-3">
        <div className="flex items-center gap-2 md:gap-3">
          {/* Mobile: botão voltar quando está fora da sidebar */}
          {isMobile && !sidebarAberta ? (
            <button
              onClick={() => { setAgrupamentoSelecionado(null); setSidebarAberta(true); }}
              className="p-1.5 text-tx-3 hover:text-tx-2 rounded-iris-sm hover:bg-surface-2 transition-colors"
              title="Voltar para grupos"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => setSidebarAberta(v => !v)}
              className="p-1.5 text-tx-3 hover:text-tx-2 rounded-iris-sm hover:bg-surface-2 transition-colors hidden md:block"
              title={sidebarAberta ? 'Ocultar painel de grupos' : 'Mostrar painel de grupos'}
            >
              {sidebarAberta ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
            </button>
          )}
          <Squares2X2Icon className="w-5 h-5 text-tx-3 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm md:text-base font-bold text-tx truncate">
              {isMobile && !sidebarAberta
                ? agrupamentoSelecionado
                  ? agrupamentoSelecionado.descricao
                  : 'Manutenção em lote'
                : 'Agrupamentos do Catálogo'}
            </h1>
            <p className="text-[10px] md:text-xs text-tx-2 truncate hidden sm:block">
              {agrupamentoSelecionado
                ? 'Clique em outro grupo para alternar · Clique novamente para fechar'
                : 'Clique em um grupo para gerenciar seus produtos'}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar de grupos */}
        {mostrarSidebar && (
          <div className={`bg-surface border-r border-line overflow-hidden
            ${isMobile ? 'w-full' : 'flex-none w-72'}`}
          >
            <ListaAgrupamentos
              agrupamentos={agrupamentos}
              agrupamentoSelecionado={agrupamentoSelecionado}
              sugestoesMap={sugestoesMap}
              carregando={carregando}
              termoBusca={termoBusca}
              onTermoBuscaChange={setTermoBusca}
              onSelecionarAgrupamento={handleSelecionarAgrupamento}
              onAbrirSugestao={handleAbrirSugestao}
              onCriarAgrupamento={() => setMostrarModalCriar(true)}
              onAbrirManutencao={handleAbrirManutencao}
              onExcluirAgrupamento={handleExcluirAgrupamento}
              onRenomearAgrupamento={handleRenomearAgrupamento}
              onRecarregar={carregarAgrupamentos}
              onRecalcularSugestoes={async () => {
                await agrupamentoService.recalcularSugestoes();
                await carregarAgrupamentos();
              }}
            />
          </div>
        )}

        {/* Área principal */}
        {mostrarConteudo && (
          <div className="flex-1 overflow-hidden">
            {agrupamentoSelecionado ? (
              <PainelGrupoSelecionado
                key={`${agrupamentoSelecionado.grupoId}-${abaInicial}`}
                agrupamento={agrupamentoSelecionado}
                termoSugeridoApi={sugestaoAtual?.termos ?? ''}
                qtdeSugeridos={sugestaoAtual?.qtdeSugeridos ?? 0}
                abaInicial={abaInicial}
                onFechar={handleFecharGrupo}
                onAgrupamentosChange={handleAgrupamentosChange}
              />
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex-none flex items-center gap-2 px-3 pt-3 pb-2 md:px-4 md:pt-4">
                  <WrenchScrewdriverIcon className="w-4 h-4 text-tx-3" />
                  <span className="text-sm font-medium text-tx-2">
                    Manutenção em lote
                  </span>
                  <span className="text-xs text-tx-3 hidden md:inline">
                    — ou selecione um grupo na barra lateral para gerenciar seus produtos
                  </span>
                </div>
                <div className="flex-1 overflow-hidden px-3 pb-3 md:px-4 md:pb-4">
                  <ManutencaoProdutosGrid
                    agrupamentos={agrupamentosAll}
                    onCriarAgrupamento={() => setMostrarModalCriar(true)}
                    onAgrupamentosChange={carregarAgrupamentos}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalCriarAgrupamento
        isOpen={mostrarModalCriar}
        onClose={() => setMostrarModalCriar(false)}
        onCriar={handleCriarConfirmado}
      />
    </div>
  );
};

export default AgrupamentoPage;
